<template>
  <v-row>
    <v-col cols="5">
      <v-row no-gutters class="align-center" style="text-align: left; justify-content: space-between;">
        <v-col cols="auto">
          <span class="text-body-2" :class="{ 'text--disabled': !isSmtpEnabled }">
            {{ $i18n.t('admin.smtpName') }}:
          </span>
        </v-col>
        <v-col cols="auto">
          <v-row no-gutters class="align-center" style="gap: 0.4rem">
            <v-col>
              <span class="text-body-2" :class="{ 'text--disabled': !isSmtpEnabled }">
                {{ settingsValues.smtp_host }}
              </span>
            </v-col>
            <v-col>
              <v-btn icon color="primary" @click="openConfigDialog" :disabled="!isSmtpEnabled">
                <v-icon>mdi-cog</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row no-gutters class="align-baseline">
        <v-col cols="auto" style="flex-grow: 1;">
          <data-input v-bind="emailToInputParams" v-model="address_to" :disabled="!isSmtpEnabled"> </data-input>
        </v-col>
        <v-col cols="auto" class="pl-10">
          <v-row no-gutters class="justify-start">
            <template v-if="!isMailSent">
              <v-col cols="auto" class="smtp-button-wrapper">
                <base-button
                  :disabled="!isSmtpEnabled || !isValidMail"
                  @click="sendMailHandler"
                  translation-path="admin.sendTestMail"
                  :loading="loading"
                ></base-button>
              </v-col>
            </template>
            <template v-else>
              <v-col>
                <v-row no-gutters>
                  <v-col cols="auto">
                    <v-icon color="success">
                      mdi-check
                    </v-icon>
                  </v-col>
                  <v-col cols="auto" class="success--text">
                    {{ $i18n.t('admin.mailSent') }}
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="auto" class="pl-5">
                    <link-underlined @click="sendMailHandler" translation-path="admin.sendAgain"> </link-underlined>
                  </v-col>
                </v-row>
              </v-col>
            </template>
          </v-row>
        </v-col>
      </v-row>
    </v-col>
    <main-input-dialog
      v-if="isConfigDialogLoaded"
      :is-visible.sync="isConfigDialogVisible"
      :loading="isConfigLoading"
      :title-translation="$i18n.t('admin.smtpConfig')"
      agree-text="dialog.agreeSave"
      dialog-width="30%"
      @agree="saveConfig"
      :inputs="inputs"
    >
      <template
        v-for="(input, idx) of inputs.filter(input => input.name !== 'smtp_security')"
        #[`${input.name}.input`]="data"
      >
        <v-row no-gutters :key="idx">
          <v-col cols="12">
            <data-input v-bind="data" v-model="configPayload[data.name]" />
          </v-col>
        </v-row>
      </template>
      <template #smtp_security.input="data">
        <v-row no-gutters>
          <v-col cols="12">
            <v-radio-group v-model="configPayload[data.name]" :disabled="data.disabled">
              <v-radio v-for="item in encryptionItems" :key="item.translationPath" :value="item.value || 'null'">
                <template #label>
                  <v-subheader
                    class="text-body-2 pa-0"
                    :class="[radioSubheaderColor]"
                    style="height: auto;"
                    v-text="getRadioLabel(item)"
                  >
                  </v-subheader>
                </template>
              </v-radio>
            </v-radio-group>
          </v-col>
        </v-row>
      </template>
    </main-input-dialog>
  </v-row>
</template>

<script>
import { call, get } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import LinkUnderlined from '@/components/LinkUnderlined';
import MainInputDialog from '@/components/MainInputDialog';

export default {
  name: 'IntegrationsMail',
  components: {
    DataInput,
    LinkUnderlined,
    MainInputDialog,
  },
  data: () => ({
    loading: false,
    isConfigLoading: false,
    isConfigDialogLoaded: false,
    isConfigDialogVisible: false,
    isMailSent: false,
    configPayload: {},
    encryptionItems: [
      {
        translationPath: 'admin.noEncryption',
        value: 'NONE',
      },
      {
        translationPath: null,
        value: 'SSL',
      },
      {
        translationPath: null,
        value: 'TLS',
      },
    ],
    emailToInputParams: {
      dataType: {
        name: 'text',
      },
      hint: 'admin.emailTo',
    },
    address_to: undefined,
  }),
  computed: {
    additionalModuleName: get('admin/additionalModules@SMTP.verbose_name'),
    radioSubheaderColor() {
      return this.isSmtpEnabled ? 'font--text' : 'text--disabled';
    },
    inputs() {
      return [
        {
          name: 'smtp_host',
          clearable: true,
          disabled: !this.isSmtpEnabled,
          translationPath: 'admin.smtpServerAddress',
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'smtp_user',
          clearable: true,
          disabled: !this.isSmtpEnabled,
          translationPath: 'admin.login',
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'smtp_sender',
          clearable: true,
          disabled: !this.isSmtpEnabled,
          translationPath: 'admin.userName',
          subtitle: 'admin.displayedUserName',
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'smtp_password',
          clearable: true,
          disabled: !this.isSmtpEnabled,
          type: 'password',
          translationPath: 'admin.password',
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'smtp_port',
          clearable: true,
          disabled: !this.isSmtpEnabled,
          translationPath: 'admin.port',
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'smtp_security',
          disabled: !this.isSmtpEnabled,
          translationPath: 'admin.encryption',
          align: 'baseline',
        },
      ];
    },
    settingsValues: get('admin/settingsValues'),
    filteredSettingValues() {
      const viewKeys = this.inputs.map(input => input.name);
      const filteredKeys = Object.keys(this.settingsValues).filter(key => viewKeys.includes(key));
      return filteredKeys.reduce((total, current) => {
        return { ...total, [current]: this.settingsValues[current] };
      }, {});
    },
    appName: get('admin/settingsValues@app_name'),
    isSmtpEnabled: get('admin/settingsValues@smtp_module_enabled'),
    isFormValid() {
      return Object.keys(this.filteredSettingValues).every(key => this.filteredSettingValues[key]);
    },
    isValidMail() {
      return this.$_validateEmail(this.address_to);
    },
  },
  methods: {
    editSingleSetting: call('admin/editSingleSetting'),
    sendMail: call('admin/sendMail'),
    openConfigDialog() {
      this.configPayload = Object.fromEntries(this.inputs.map(input => [input.name, this.settingsValues[input.name]]));
      this.isConfigDialogLoaded = this.isConfigDialogVisible = true;
    },
    async saveConfig() {
      this.isConfigLoading = true;
      try {
        Object.entries(this.configPayload).forEach(([key, value]) => {
          if (value !== this.settingsValues[key]) {
            this.editSingleSetting({
              key,
              body: {
                value,
              },
            });
          }
        });
        this.isConfigDialogVisible = false;
      } catch {
        //
      } finally {
        this.isConfigLoading = false;
      }
    },
    async sendMailHandler() {
      console.log('~ sendMailHandler');
      try {
        this.loading = true;
        await this.sendMail({
          address_to: this.address_to,
          content: this.$i18n.t('admin.emailTestMessage', { app_name: this.appName }),
          subject: this.$i18n.t('admin.emailTestSubject'),
        });
        setTimeout(() => {
          this.loading = false;
          this.isMailSent = true;
        }, 500);
      } catch (error) {
        this.loading = false;
      }
    },
    getRadioLabel(item) {
      return item.translationPath ? this.$i18n.t(item.translationPath) : item.value.toUpperCase();
    },
  },
};
</script>

<style scoped lang="scss">
.border {
  @include border(all, 2px);
  border-style: dashed;
}
::v-deep .smtp-button-wrapper .v-btn {
  margin: 0 !important;
}
</style>
